// BUILD-046 increment 1a: how the cells of an Assigned by Me row are DRAWN. What each cell says is issuedRowModel.ts's (and assignmentAgeModel.ts's for the age); this file draws it.
//
//   ModeBadge       the small Single / Anyone / Everyone badge beside the task title (a badge, not a column).
//   ResponsibleView the "Assigned to" cell: the people as INITIALS CIRCLES (increment 1b-i: AvatarGroup - up to three, then "+N"; the full name is the circle's accessible name, its tooltip on hover and
//                   keyboard focus and its pinned popover on tap; no name is written beside a circle), a group as "Anyone in <Group>" (a group is not a person, so its name stays text), or "Not
//                   available". An Everyone request adds the disclosure button (`toggle`, drawn by the row) that opens its recipients without opening any assignment action.
//                   Only this component changed for the circles; the Cell around it, the headings and the sorting did not.
//   ProgressView    the "Status / progress" cell: a status word, "Unclaimed", or the count "0 of 2 done" - never a person.
//   PriorityView / DueView   the request's priority and Task due date; a figure the server did not supply is "Not available" with the reason on hover. The due words and the Overdue
//                   tone come from the page's one Phoenix day (usePhoenixDay) and THE one overdue rule (overdueRule.ts): nothing here decides overdue itself.
//   AssignedAgeView the "Assigned / age" cell: the Phoenix date of the ORIGINAL assignment and a quiet, neutral age, written against the page's Phoenix day signal so it refreshes
//                   with the day (no re-read of the list). The age is never red and never reaches the overdue rule.
//   RecipientsToggle / RecipientsList   the disclosure (aria-expanded, a real button, keyboard operable) and the list it opens.
import type { ReactNode } from 'react';
import { describeAssignedAge } from './assignmentAgeModel';
import type { Density } from './densityStore';
import type { ModeBadge as ModeBadgeData, DueData, PriorityCell, ProgressCell, ResponsibleCell } from './issuedRowModel';
import { NOT_AVAILABLE } from './issuedRowModel';
import { isOverdueTask } from './overdueRule';
import { AvatarGroup } from './PersonAvatar';
import { DueMark, PriorityMark } from './TaskTable';
import { TruncatedText } from './TruncatedText';
import { usePhoenixDay } from './usePhoenixDay';

// Whole class strings (not built from pieces) so the style scanner sees them.
const BADGE_CLASS: Readonly<Record<ModeBadgeData['mode'], string>> = Object.freeze({
  single: 'border-gray-300 bg-gray-50 text-gray-700',
  anyone: 'border-sky-200 bg-sky-50 text-sky-800',
  everyone: 'border-violet-200 bg-violet-50 text-violet-800',
});

/** The small mode badge beside the title: "Single", "Anyone" or "Everyone", with what it means on hover and for assistive technology. */
export function ModeBadge({ badge }: { badge: ModeBadgeData }) {
  // Owner correction 2026-10-24 (item 4): "Single" is the ordinary case and says nothing a person needs; only Anyone and Everyone, which explain completion behavior, are drawn.
  if (badge.mode === 'single') return null;
  return (
    <span data-testid="mode-badge" data-mode={badge.mode} title={badge.title} className={`inline-flex shrink-0 items-center rounded border px-1.5 py-px text-[11px] font-medium leading-4 ${BADGE_CLASS[badge.mode]}`}>
      {badge.label}
      <span className="sr-only">: {badge.title}</span>
    </span>
  );
}

export interface ResponsibleViewProps {
  cell: ResponsibleCell;
  density: Density;
  /** The Everyone disclosure button (drawn by the row, which owns its state), or null. */
  toggle?: ReactNode;
}

/** The "Assigned to" cell: who is responsible now. */
export function ResponsibleView({ cell, density, toggle }: ResponsibleViewProps) {
  if (cell.kind === 'none') {
    return (
      <span className="text-gray-600" title={cell.title}>
        {cell.text}
      </span>
    );
  }
  if (cell.kind === 'group') {
    // Compact cuts it to one line: the full words (and who it was sent to) are on hover and on keyboard focus there. Comfortable wraps it.
    if (density === 'compact') {
      return (
        <TruncatedText text={`${cell.text}. ${cell.title}`} block>
          <span className="text-gray-600">{cell.prefix}</span>
          {cell.labels.join(', ')}
        </TruncatedText>
      );
    }
    return (
      <span className="block min-w-0 break-words" title={`${cell.text}. ${cell.title}`}>
        <span className="text-gray-600">{cell.prefix}</span>
        {cell.labels.join(', ')}
      </span>
    );
  }
  // The people are circles (the stable employee id picks the colour); no name is written beside them. The expansion of an Everyone request lists each recipient with a circle AND the name.
  return (
    <span className="flex min-w-0 items-center gap-1" data-testid="issued-assigned-to">
      <AvatarGroup people={cell.people.map((person) => ({ id: person.employeeId, name: person.name }))} size={density === 'compact' ? 'compact' : 'comfortable'} label={cell.title} />
      {toggle}
    </span>
  );
}

/** The "Status / progress" cell. `compact` keeps it to one line: the outcome note of an Everyone request is then the cell's hover text instead of a second line. */
export function ProgressView({ cell, density }: { cell: ProgressCell; density: Density }) {
  if (cell.kind === 'loading') {
    return (
      <span className="text-gray-600" role="status">
        {cell.text}
      </span>
    );
  }
  if (cell.kind === 'unavailable') {
    return (
      <span role="alert" className="text-red-700">
        {cell.text}
        {cell.detail !== null ? `: ${cell.detail}` : ''}
      </span>
    );
  }
  if (cell.kind === 'none') {
    return <span className="text-gray-600">{cell.text}</span>;
  }
  return (
    <span title={cell.detail ?? undefined}>
      <strong className="font-medium text-gray-900">{cell.text}</strong>
      {cell.detail !== null && density !== 'compact' && <span className="block text-xs text-gray-600">{cell.detail}</span>}
    </span>
  );
}

/**
 * "Varies": an Everyone request whose recipients' own tasks differ. The words are the cell; each recipient's value is the hover text AND the accessible name, and the cell takes focus so the details are
 * reachable by keyboard too. The same list is in the recipients expansion under the Assigned to cell.
 */
function VariesMark({ title }: { title: string }) {
  return (
    <span tabIndex={0} title={title} data-testid="varies-mark" className="cursor-help whitespace-nowrap text-gray-800 underline decoration-dotted underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]">
      Varies
      <span className="sr-only">. {title}</span>
    </span>
  );
}

/** The request's priority: the coloured dot and its word (PriorityMark), the dash when it has none, "Varies" when an Everyone request's recipients differ, "Not available" when the server did not supply it. */
export function PriorityView({ cell }: { cell: PriorityCell }) {
  if (cell.kind === 'varies') return <VariesMark title={cell.title} />;
  if (cell.kind === 'unavailable') {
    return (
      <span className="text-xs text-gray-600" title={cell.title}>
        {NOT_AVAILABLE}
      </span>
    );
  }
  return <PriorityMark priority={cell.kind === 'priority' ? cell.value : null} />;
}

/** The request's Task due date: the page's Phoenix due words, Overdue by THE one rule at the page's Phoenix day, "Not available" when the server did not supply it. */
export function DueView({ data, stack = false }: { data: DueData; stack?: boolean }) {
  const phoenixDay = usePhoenixDay();
  if (data.kind === 'varies') return <VariesMark title={data.title} />;
  if (data.kind === 'unavailable') {
    return (
      <span className="text-xs text-gray-600" title={data.title}>
        {NOT_AVAILABLE}
      </span>
    );
  }
  return <DueMark dueDate={data.dueDate === '' ? null : data.dueDate} overdue={isOverdueTask(data.overdueInput, phoenixDay)} stack={stack} />;
}

/**
 * The "Assigned / age" cell: "Oct 19" and a quiet "5 days". The day comes from the page's ONE Phoenix-day signal (usePhoenixDay), so the age moves at 00:00 Phoenix while the page
 * stays open and the list is not read again. Neutral grey: the age is information, never a warning.
 */
export function AssignedAgeView({ createdAt, density }: { createdAt: string | null; density: Density }) {
  const phoenixDay = usePhoenixDay();
  const age = describeAssignedAge(createdAt, phoenixDay);
  if (!age.available) {
    return (
      <span className="text-xs text-gray-600" title={age.title}>
        {age.dateText}
      </span>
    );
  }
  return (
    <span title={age.title} data-testid="assigned-age" className={density === 'compact' ? 'whitespace-nowrap' : undefined}>
      <span className="text-gray-800">{age.dateText}</span>
      {density === 'compact' ? <span className="text-xs text-gray-500"> {'\u00b7'} {age.ageText}</span> : <span className="block text-xs text-gray-500">{age.ageText}</span>}
    </span>
  );
}

/** The chevron button that opens and closes the recipients of an Everyone request. A real button: Tab reaches it, Enter and Space operate it. */
export function RecipientsToggle({ expanded, controls, label, onToggle }: { expanded: boolean; controls: string; label: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={expanded ? controls : undefined}
      aria-label={`${expanded ? 'Hide' : 'Show'} recipients: ${label}`}
      title={expanded ? 'Hide recipients' : 'Show recipients'}
      data-testid="recipients-toggle"
      onClick={onToggle}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
    >
      <svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`}>
        <path d="M2.5 4.5L6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}
